import * as Popover from '@radix-ui/react-popover'
import { ChangeEvent, KeyboardEvent } from 'react'

import { Slot, usePopover } from '@/shared/lib'
import { Icon, TextField, type InputProps } from '@/shared/ui'

import { KeywordSearchHistory } from '../../model/keyword-search-history-store/history'
import { KeywordSearchHistoryPopup } from '../keyword-search-history-popup'

type Props = Omit<
  InputProps,
  'onChange' | 'ref' | 'value' | 'onFocus' | 'onBlur'
> & {
  value: string
  inputClassName?: string
  popupAlign?: 'start' | 'end'
  popupAlignOffset?: number
  popupSideOffset?: number
  onChange: (value: string) => void
  onFocus?: () => void
  onBlur?: () => void
  onEnterKeyDown?: () => void
  onRecentHistoryClick?: (history: KeywordSearchHistory) => void
  onRecommendKeywordClick?: (keyword: string) => void
}

export const KeywordInput = ({
  value,
  popupAlign = 'start',
  popupAlignOffset = 0,
  popupSideOffset = 0,
  onChange,
  onFocus,
  onBlur,
  onEnterKeyDown,
  onRecentHistoryClick,
  onRecommendKeywordClick,
  className,
  inputClassName,
  ...props
}: Props) => {
  const {
    isOpen,
    ref: triggerRef,
    open,
    close,
    handleOutsideClick: handlePopoverOutsideClick,
  } = usePopover<HTMLDivElement>()

  const handleKeyDown = (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.nativeEvent.isComposing) return

    if (event.key === 'Enter') {
      event.preventDefault()
      onEnterKeyDown?.()
    } else if (event.key === 'Escape') {
      close()
    } else if (event.key === 'Tab') {
      onBlur?.()
    }
  }

  const handleRecentHistoryClick = (history: KeywordSearchHistory) => {
    onRecentHistoryClick?.(history)
    close()
    onBlur?.()
  }

  const handleRecommendKeywordClick = (keyword: string) => {
    onRecommendKeywordClick?.(keyword)
    close()
    onBlur?.()
  }

  const handlePopupClose = () => {
    close()
  }

  const handleInputChange = (event: ChangeEvent<HTMLInputElement>) => {
    onChange(event.target.value)

    if (!isOpen) {
      open()
    }
  }

  const handleInputReset = () => {
    onChange('')
  }

  const handleInputFocus = () => {
    open()
    onFocus?.()
  }

  return (
    <Popover.Root open={isOpen}>
      <Popover.Trigger asChild>
        <div ref={triggerRef} className={className}>
          <TextField
            {...props}
            value={value}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={handleInputFocus}
            onReset={handleInputReset}
            className={inputClassName}
          >
            <Slot name="left">
              <Icon
                name="search"
                size={24}
                className="text-black"
                strokeWidth={1.5}
              />
            </Slot>
          </TextField>
        </div>
      </Popover.Trigger>
      {isOpen && (
        <KeywordSearchHistoryPopup
          onItemClick={handleRecentHistoryClick}
          onRecommendKeywordClick={handleRecommendKeywordClick}
          onClose={handlePopupClose}
          onInteractOutside={handlePopoverOutsideClick}
          align={popupAlign}
          alignOffset={popupAlignOffset}
          sideOffset={popupSideOffset}
        />
      )}
    </Popover.Root>
  )
}
